import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api';
import ImageDrop from '../components/ImageDrop';
function ssimVerdict(s) {
    if (s >= 0.95)
        return 'great';
    if (s >= 0.85)
        return 'good';
    if (s >= 0.65)
        return 'meh';
    return 'bad';
}
export default function Compare() {
    const { t } = useTranslation();
    const [mode, setMode] = useState('frame');
    // ── modo frame ──────────────────────────────────────────────────────
    const [ref, setRef] = useState(null);
    const [recv, setRecv] = useState(null);
    const [result, setResult] = useState(null);
    // ── modo vídeo ──────────────────────────────────────────────────────
    const [vref, setVref] = useState(null);
    const [vrecv, setVrecv] = useState(null);
    const [stride, setStride] = useState(10);
    const [vresult, setVresult] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const submitFrame = async (e) => {
        e.preventDefault();
        if (!ref || !recv)
            return;
        setBusy(true);
        setError(null);
        setResult(null);
        const fd = new FormData();
        fd.append('reference', ref);
        fd.append('received', recv);
        try {
            const r = await api.post('/compare', fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setResult(r.data);
        }
        catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        }
        finally {
            setBusy(false);
        }
    };
    const submitVideo = async (e) => {
        e.preventDefault();
        if (!vref || !vrecv)
            return;
        setBusy(true);
        setError(null);
        setVresult(null);
        const fd = new FormData();
        fd.append('reference', vref);
        fd.append('received', vrecv);
        fd.append('stride', String(stride));
        try {
            const r = await api.post('/compare/video', fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setVresult(r.data);
        }
        catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        }
        finally {
            setBusy(false);
        }
    };
    const verdict = result ? ssimVerdict(result.ssim) : null;
    const vverdict = vresult ? ssimVerdict(vresult.ssim.mean) : null;
    return (_jsxs("section", { className: "prose", children: [_jsx("h1", { children: t('compare.title') }), _jsx("p", { className: "muted", children: t('compare.subtitle') }), _jsxs("div", { className: "chip-row", role: "tablist", children: [_jsx("button", { type: "button", className: `chip ${mode === 'frame' ? 'active' : ''}`, onClick: () => { setMode('frame'); setError(null); }, children: t('compare.modeFrame') }), _jsx("button", { type: "button", className: `chip ${mode === 'video' ? 'active' : ''}`, onClick: () => { setMode('video'); setError(null); }, children: t('compare.modeVideo') })] }), mode === 'frame' && (_jsxs("form", { onSubmit: submitFrame, className: "compare-form", children: [_jsxs("div", { className: "compare-pair", children: [_jsx(ImageDrop, { label: t('compare.reference'), hint: t('compare.referenceHint'), file: ref, onFile: setRef }), _jsx(ImageDrop, { label: t('compare.received'), hint: t('compare.receivedHint'), file: recv, onFile: setRecv })] }), error && _jsx("p", { className: "error", children: error }), _jsx("button", { type: "submit", className: "btn btn-primary", disabled: busy || !ref || !recv, children: busy ? t('common.loading') : t('compare.submit') })] })), mode === 'frame' && result && (_jsxs("div", { className: "card metric-card", "data-verdict": verdict, children: [_jsxs("div", { className: "metric", children: [_jsx("span", { className: "metric-label", children: "PSNR" }), _jsx("span", { className: "metric-value", children: result.psnr_inf ? '∞' : `${result.psnr_db?.toFixed(2)} dB` }), _jsx("span", { className: "muted", children: t('compare.psnrHint') })] }), _jsxs("div", { className: "metric", children: [_jsx("span", { className: "metric-label", children: "SSIM" }), _jsx("span", { className: "metric-value", children: result.ssim.toFixed(4) }), _jsx("span", { className: "muted", children: t(`compare.verdict.${verdict}`) })] })] })), mode === 'video' && (_jsxs("form", { onSubmit: submitVideo, className: "compare-form", children: [_jsx("p", { className: "muted", children: t('compare.videoSubtitle') }), _jsxs("div", { className: "compare-pair", children: [_jsxs("label", { className: "file-field", children: [t('compare.videoReference'), _jsx("input", { type: "file", accept: "video/*", onChange: (e) => setVref(e.target.files?.[0] ?? null) })] }), _jsxs("label", { className: "file-field", children: [t('compare.videoReceived'), _jsx("input", { type: "file", accept: "video/*", onChange: (e) => setVrecv(e.target.files?.[0] ?? null) })] })] }), _jsxs("label", { children: [t('compare.stride'), _jsx("input", { type: "number", min: 1, max: 120, value: stride, onChange: (e) => setStride(Number(e.target.value)) }), _jsx("span", { className: "muted", children: t('compare.strideHint') })] }), error && _jsx("p", { className: "error", children: error }), _jsx("button", { type: "submit", className: "btn btn-primary", disabled: busy || !vref || !vrecv, children: busy ? t('common.loading') : t('compare.submit') })] })), mode === 'video' && vresult && (_jsxs("div", { className: "card metric-card", "data-verdict": vverdict, children: [_jsxs("div", { className: "metric", children: [_jsxs("span", { className: "metric-label", children: ["PSNR (", t('compare.mean'), ")"] }), _jsx("span", { className: "metric-value", children: vresult.psnr_db.mean === null ? '∞' : `${vresult.psnr_db.mean.toFixed(2)} dB` }), _jsxs("span", { className: "muted", children: ["min ", vresult.psnr_db.min ?? '∞', " \u00B7 p5 ", vresult.psnr_db.p5 ?? '∞'] })] }), _jsxs("div", { className: "metric", children: [_jsxs("span", { className: "metric-label", children: ["SSIM (", t('compare.mean'), ")"] }), _jsx("span", { className: "metric-value", children: vresult.ssim.mean.toFixed(4) }), _jsxs("span", { className: "muted", children: [t(`compare.verdict.${vverdict}`), " \u00B7 ", t('compare.framesCompared', { n: vresult.frames_compared })] })] })] }))] }));
}
